import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { classesAPI } from '../../services/classService';
import { admissionsAPI } from '../../services/admissionService';
import { couponsAPI } from '../../services/couponService';

const ProcessAdmissionDialog = ({ admission, onSuccess, onHide, visible, toastRef, parents = [] }) => {

  const [form, setForm] = useState({
    fullName: "",
    dateOfBirth: null,
    gender: "",
    admissionDate: null,
    classId: "",
    sectionId: "",
    parentId: "",
    imageUrl: "",
    admissionerId: "",
    bloodGroup: "",
    couponId: ""
  });
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const genderOptions = [
    { label: 'Male', value: 'male' },
    { label: 'Female', value: 'female' },
    { label: 'Other', value: 'other' }
  ];

  useEffect(() => {
    if (visible) {
      loadClasses();
      loadCoupons();
    }
  }, [visible]);

  useEffect(() => {
    if (admission && visible) {
      setForm({
        fullName: admission.firstName || "",
        dateOfBirth: admission.dateOfBirth ? new Date(admission.dateOfBirth) : null,
        gender: admission.gender || "",
        admissionDate: admission.admissionDate ? new Date(admission.admissionDate) : null,
        classId: admission.classId || "",
        sectionId: admission.sectionId || "",
        parentId: admission.parentId || "",
        imageUrl: admission.imageUrl || "",
        admissionerId: admission.admissionerId || "",
        bloodGroup: admission.bloodGroup || "",
        couponId: admission.couponId || ""
      });
      if (admission.classId) {
        loadSections(admission.classId);
      }
    }
  }, [admission, visible]);

  const loadClasses = async () => {
    try {
      const res = await classesAPI.getAllclasses();
      if (res?.data?.success) setClasses(res.data.data || []);
      else setClasses([]);
    } catch (err) {
      console.error('loadClasses error', err);
      setClasses([]);
    }
  };

  const loadCoupons = async () => {
    try {
      const res = await couponsAPI.getUnUsedCoupons();
      const couponsData = res?.data?.coupons || [];
      setCoupons(couponsData);
    } catch (err) {
      console.error('loadCoupons error', err);
    }
  };

  const loadSections = async (classId) => {
    if (!classId) {
      setSections([]);
      return;
    }
    try {
      const res = await classesAPI.getsectionsbyclass(classId);
      if (res?.data?.success) setSections(res.data.data || []);
      else setSections([]);
    } catch (err) {
      console.error('loadSections error', err);
      setSections([]);
    }
  };

  const handleClassChange = (classId) => {
    setForm({ ...form, classId, sectionId: "" });
    loadSections(classId);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setForm({ ...form, imageUrl: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const submit = async () => {
    if (!admission?.id) return;
    setSubmitting(true);
    try {
      const payload = {
        admissionId: admission.id,
        fullName: form.fullName,
        // lastName: form.lastName,
        dob: form.dateOfBirth,
        gender: form.gender,
        // admissionDate: form.admissionDate,
        classId: parseInt(form.classId),
        sectionId: parseInt(form.sectionId),
        bloodGroup: form.bloodGroup,
        parentId: parseInt(form.parentId),
        // photoUrl: form.imageUrl,
        couponId: form.couponId ? parseInt(form.couponId) : null
      };
      const res = await admissionsAPI.processAdmission(payload);
      toastRef?.current?.show({ severity: 'success', summary: 'Success', detail: res.data?.message || 'Admission processed successfully' });
      onSuccess();
      onHide();
    } catch (err) {
      console.error('Failed to process admission:', err);
      toastRef?.current?.show({ severity: 'error', summary: 'Error', detail: err?.message || 'Failed to process admission' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog header="Process Admission" visible={visible} onHide={onHide} style={{ width: '900px' }} footer={(
      <div className="flex justify-between items-center mt-4">
        <Button label="Cancel" icon="pi pi-times" onClick={onHide} className="bg-gray-400 text-white rounded-lg px-4 py-2 hover:bg-gray-500 transition-all" />
        <Button label="Process Admission" icon="pi pi-check" onClick={submit} loading={submitting} className="bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg px-4 py-2 shadow-md hover:shadow-lg transition-all" />
      </div>
    )}>
      <div className="space-y-6 p-2">

        <div className="flex flex-row justify-between">
          <div className="flex mb-4 relative group">
            <img
              src={form.imageUrl || "https://via.placeholder.com/150"}
              alt="Student"
              className="w-32 h-32 object-cover rounded-full border-4 border-white shadow-lg cursor-pointer"
              onClick={() => fileInputRef.current.click()}
            />
            <div
              className="absolute inset-0 w-32 h-32 rounded-full bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              onClick={() => fileInputRef.current.click()}
            >
              <i className="pi pi-camera text-white text-2xl"></i>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              accept="image/*"
              className="hidden"
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <p className="text-sm font-semibold text-blue-900 pb-4">Admission Number: ADM0{admission?.id}</p>
            <p className="text-sm font-semibold text-blue-900">Admission Date: {new Date(admission?.admissionDate).toLocaleDateString()}</p>
          </div>

        </div>

        <div className="border-b pb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
              <InputText value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} className="w-full p-3 bg-gray-100 border border-gray-300 rounded-lg text-gray-700" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Coupon (Optional)</label>
                <Dropdown value={form.couponId} options={coupons.map(c => ({ label: `${c.code} - ${c.feeType} - ${c.discountPct}%`, value: c.id }))}
                  onChange={(e) => setForm({ ...form, couponId: e.value })} placeholder="Select coupon" className="w-full border border-gray-200 rounded-lg"
                  filter filterPlaceholder="Search by code, fee type, or discount" showClear />
              
            </div>
          </div>
        </div>

        <div className="border-b pb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
              <Calendar value={form.dateOfBirth} onChange={(e) => setForm({ ...form, dateOfBirth: e.value })}
                showIcon dateFormat="dd-mm-yy" className="w-full border border-gray-200 rounded-lg" showClear />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Gender *</label>
              <Dropdown value={form.gender} options={genderOptions} onChange={(e) => setForm({ ...form, gender: e.value })} placeholder="Select gender" className="w-full border border-gray-200 rounded-lg" />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-800"> Blood Group</label>
              <Dropdown value={form.bloodGroup}
                options={[
                  { label: 'A+', value: 'A+' },
                  { label: 'A-', value: 'A-' },
                  { label: 'B+', value: 'B+' },
                  { label: 'B-', value: 'B-' },
                  { label: 'AB+', value: 'AB+' },
                  { label: 'AB-', value: 'AB-' },
                  { label: 'O+', value: 'O+' },
                  { label: 'O-', value: 'O-' }
                ]}
                onChange={(e) => setForm({ ...form, bloodGroup: e.value })} placeholder="Select blood group" className="w-full text-sm border border-gray-200 rounded-lg" panelClassName="border border-gray-200 rounded-lg shadow-lg" showClear />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Class *</label>
              <Dropdown value={form.classId} options={classes.map(c => ({ label: c.name || c.className, value: c.id }))}
                onChange={(e) => handleClassChange(e.value)} placeholder="Select class" className="w-full border border-gray-200 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Section </label>
              <Dropdown value={form.sectionId} options={sections.map(s => ({ label: s.name || s.section_name, value: s.id }))}
                onChange={(e) => setForm({ ...form, sectionId: e.value })} placeholder="Select section" disabled={!form.classId} className="w-full border border-gray-200 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Parent *</label>
              <Dropdown value={form.parentId} options={parents.map(p => ({ label: `${p.name}`, value: p.id }))}
                onChange={(e) => setForm({ ...form, parentId: e.value })} placeholder="Select parent" className="w-full border border-gray-200 rounded-lg" />
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ProcessAdmissionDialog;
